<template>
  <div
    ref="contentContainerSrc"
    :class="['content-container']"
    :style="{
      '--font-multiplier': String(fontMultiplier),
      ...(isShrinkTarget ? { 'min-height': '0px' } : {}),
    }"
    :dir="isRightToLeft ? 'rtl' : 'ltr'"
  >
    <GeneralMessageChatText
      v-for="(messageDetails, index) in chatContents"
      ref="refsText"
      :key="getKey(messageDetails)"
      :class="['origin-text', getClassTextSource(messageDetails, index)]"
      :dir="isRightToLeft ? 'rtl' : 'ltr'"
      :style="styleParagraph"
      :is-last-sub-id="index === chatContents.length - 1"
      :message-details
      @edit="openModalEdit"
    />
    <Teleport to="body">
      <ModalBig title="원문 수정" v-if="isVisibleModalEdit" @close="closeModalEdit">
        <GeneralMessageCommonModalEdit :close-modal-edit :message-details :data-flag="'src'" :language-code-set />
      </ModalBig>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, Ref, ref, useTemplateRef, watch, onMounted, CSSProperties } from 'vue';

import GeneralMessageChatText from '@/components/domains/chat/chat-general/GeneralMessageChatText.vue';
import GeneralMessageCommonModalEdit from '@/components/domains/chat/chat-general/GeneralMessageCommonModalEdit.vue';
import ModalBig from '@/components/shared/composite/ModalBig.vue';
import { useMessageTextCommon } from '@/composables/domains/chat/useMessageTextCommon';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { MessageText } from '@/types/socket';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { ij_isLastIsOwn, ij_isLastMessage, ij_isOldMessage } from '@/utils/injectionKeys.js';

const userStore = useUserStore();
const socketStore = useSocketStore();

const props = withDefaults(
  defineProps<{
    chatContents: MessageText[];
    isSourcePlaying: boolean;
    isComplete: boolean | undefined;
    isPendingSrc: boolean;
    languageCodeSet: Set<LanguageCode>;
    usingAutoHeight?: boolean;
    isDmMessage: boolean;
  }>(),
  {
    chatContents: () => [],
    isSourcePlaying: false,
    isComplete: false,
    isPendingSrc: false,
    languageCodeSet: new Set(),
    usingAutoHeight: true,
    isDmMessage: false,
  }
);

const isLastIsOwn = inject(ij_isLastIsOwn) as Ref<boolean>; // 이 메세지가 마지막인지
const isLastMessage = inject(ij_isLastMessage) as Ref<boolean>; // 이 메세지가 마지막인지
const isOldMessage = inject(ij_isOldMessage) as Ref<boolean>; // 이 메세지가 마지막인지

const contentContainerSrc = ref<HTMLElement>();
const refsText = useTemplateRef('refsText');
const messageDetails = ref({}) as Ref<MessageText>;
const isVisibleModalEdit = ref(false); // 수정 모달 보임 여부

// 오른쪽 정렬 언어인지 반환
const isRightToLeft = computed(() => {
  const listLangAlignRight: LanguageCode[] = ['ar']; // 아랍어,
  return listLangAlignRight.some((lang) => props.languageCodeSet.has(lang));
});

// 글자 크기 배율
const fontMultiplier = computed(() => userStore.userPreferences.textSizeMultiplier);

// 줄어들어야 할 대상인지
const isShrinkTarget = computed(() => {
  return isOldMessage.value || (!isLastMessage.value && props.isComplete);
});

const isEveryLanguageSpaced = computed(() => {
  return [...props.languageCodeSet].every((code) => !isLanguageNoSpace(code));
});

const styleParagraph = computed((): CSSProperties => {
  return {
    wordBreak: isEveryLanguageSpaced.value ? 'keep-all' : 'break-all',
  };
});

// 한 줄의 높이
const getlineHeight = () => {
  const el = refsText.value?.[0]?.refTextSubId;
  if (!el) return 16 * 1.5;

  const style = getComputedStyle(el);
  return parseFloat(style.fontSize) * 1.5;
};

// 수정 모달 닫기
const closeModalEdit = () => {
  isVisibleModalEdit.value = false;
};

// 수정 모달 띄우기
const openModalEdit = (messageClicked: MessageText) => {
  if (!userStore.userInfo.isTester) return;
  if (socketStore.connectInfo!.connectionType !== 'GENERAL') return;
  const isEditable = messageClicked.isEditable || !isLastIsOwn.value;
  if (!isEditable) return;
  messageDetails.value = messageClicked;
  isVisibleModalEdit.value = true;
};

//문장별 클래스
const getClassTextSource = (content: MessageText, index: number): object => {
  const isLastPart = index === props.chatContents.length - 1;

  return {
    'sound-playing': props.isSourcePlaying,

    'highlight-origin': isLastIsOwn.value && isLastPart && content.highlight,

    'highlight-origin-fadeout': isLastIsOwn.value && !!content.fadeout,
  };
};

const { updateContainerHeight, shrinkBubble, getKey } = useMessageTextCommon({
  contentContainer: contentContainerSrc,
});

onMounted(() => {
  if (props.isDmMessage) {
    return;
  }
  if (isLastIsOwn.value && props.usingAutoHeight && !isShrinkTarget.value) {
    updateContainerHeight(getlineHeight());
  }
});

// 내용이 바뀔 때마다 버블 높이 조절
watch(
  () => props.chatContents,
  () => {
    if (props.isDmMessage) {
      return;
    }
    if (isLastIsOwn.value && props.usingAutoHeight) {
      updateContainerHeight(getlineHeight()); // 높이 조절
    }
  },
  { flush: 'post' }
);

// 글자 크기 변경 시 높이 조절
watch(fontMultiplier, () => {
  shrinkBubble(); // 버블 줄이기
});
</script>

<style scoped lang="scss">
.text-align-right {
  text-align: right;
}

.content-container {
  overflow: hidden;
  min-height: 1px;
  height: auto;
  padding: 0;
  transition:
    height 0.7s ease,
    min-height 0.7s ease;
  text-align: start;
}

/* 오리지널 - 텍스트 */
.origin-text {
  display: inline;
  line-height: 160%;
  color: var(--font-chat-going-text-source);
  border-radius: 4px;
  font-size: calc($font-size-p * var(--font-multiplier, 1));
  unicode-bidi: isolate;
}

/* 오리지널 - 소리 Play */
.sound-playing {
  color: var(--font-chat-going-text-source);
}

/* 오리지널 - 큰 텍스트 */
.bigger-text .origin-text {
  font-size: calc($font-size-h4 * var(--font-multiplier, 1));
}

/* 오리지널 - 통역/입력중인 텍스트 */
.highlight-origin {
  display: inline;
  color: var(--font-chat-going-text-source);
  animation: none;
  opacity: 0.5;
}

/* 오리지널 - 통역 완료된 텍스트 */
.highlight-origin-fadeout {
  display: inline;
  color: var(--font-chat-going-text-source);
  animation: fadeIn 3s ease-in-out forwards;
}

@keyframes fadeIn {
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>
